import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect, useRef } from "react";

import { setTrackAnalytic } from "@skydio/mission-logic";

import { IS_DEV } from "app/env";

type PropertyValue = string | number | object;

export function setGlobalEventProperty(k: string, v: PropertyValue) {
  datadogRum.setGlobalContextProperty(k, v);
  datadogLogs.setGlobalContextProperty(k, v);
}

/**
 * Should be used for direct user interactions, or low-frequency state transitions within
 * the context of a user's session. Only supports large (minute) rollup windows. If you're
 * trying to tracking high frequency events, or events not related to user actions, use
 * `trackAnalytic` instead.
 *
 * @param name event name
 * @param payload extra information for the event
 */
export function trackUserAction(name: string, payload?: object) {
  try {
    if (IS_DEV) {
      console.debug("tracking event", { name, event: payload });
    } else {
      datadogRum.addAction(name, payload);
    }
  } catch (e) {
    console.error(e);
    if (IS_DEV) {
      // In dev, throw the error so we are aware there's an issue
      throw e;
    }
  }
}

/**
 * Should be used for high frequency analytics like periodic stats collection. Supports small
 * (second) rollup windows. If you're trying to track events related to user actions, use
 * `trackUserAction` instead.
 *
 * @param log text content
 * @param payload extra information for the log
 */
export function trackAnalytic(log: string, payload?: object) {
  try {
    if (IS_DEV) {
      console.debug("tracking log event", {
        log,
        event: payload,
        ddContext: {
          logs: datadogLogs.getGlobalContext(),
          rum: datadogRum.getGlobalContext(),
        },
        user: datadogLogs.getUser() ?? datadogRum.getUser(),
      });
    }
    datadogLogs.logger.info(log, payload);
  } catch (e) {
    console.error(e);
    if (IS_DEV) {
      // In dev, throw the error so we are aware there's an issue
      throw e;
    }
  }
}

/**
 * Hook that tracks an event when a condition becomes true
 */
export function useTrackUserAction(condition: boolean, name: string, payload?: object) {
  const previousConditionRef = useRef(false);
  // Effect to track event when the condition becomes true
  useEffect(() => {
    if (condition && previousConditionRef.current === false) {
      trackUserAction(name, payload);
      previousConditionRef.current = true;
      return;
    }
    if (!condition && previousConditionRef.current === true) {
      previousConditionRef.current = false;
      return;
    }
  }, [condition, name, payload]);
}

// Wire up mission-logic simulation analytics with the real trackAnalytic implementation
setTrackAnalytic(trackAnalytic);
